import React, { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';

/**
 * Folder name — same click-to-type pattern as Flow mission cards
 * (`EditableCardName`): always a textarea, display vs edit via class.
 */
const AtlasFolderName = ({
  folderId,
  name,
  onRename,
  isEditingTitle = false,
  onFinishEdit,
  placeholderName = 'New folder',
  editHint = 'Type a folder name...',
}) => {
  const displayValue = name || placeholderName;
  const isPlaceholder = !name || name === placeholderName;

  const [isLocalEditing, setIsLocalEditing] = useState(false);
  const isEditing = isEditingTitle || isLocalEditing;
  const [editValue, setEditValue] = useState(name || '');
  const inputRef = useRef(null);
  const clickCursorPosRef = useRef(null);

  const collapseSelection = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    try {
      el.setSelectionRange(0, 0);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!isEditing) setEditValue(name || '');
  }, [name, isEditing]);

  useEffect(() => {
    if (!isEditingTitle || !isPlaceholder) return;
    setEditValue('');
  }, [isEditingTitle, isPlaceholder]);

  useEffect(() => {
    if (!isEditing) return undefined;
    const rafId = requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      const len = el.value.length;
      if (clickCursorPosRef.current !== null) {
        const pos = clickCursorPosRef.current;
        el.setSelectionRange(pos, pos);
      } else {
        el.setSelectionRange(len, len);
      }
      clickCursorPosRef.current = null;
    });
    return () => cancelAnimationFrame(rafId);
  }, [isEditing]);

  useLayoutEffect(() => {
    if (!isEditing) {
      collapseSelection();
      if (document.activeElement === inputRef.current) {
        inputRef.current.blur();
      }
    }
  }, [isEditing, displayValue, collapseSelection]);

  useEffect(() => {
    const onDocMouseDown = (event) => {
      const el = inputRef.current;
      if (!el || el.contains(event.target)) return;
      const isActive = document.activeElement === el;
      const hasRange = el.selectionStart !== el.selectionEnd;
      if (!isActive && !hasRange) return;
      collapseSelection();
    };
    document.addEventListener('mousedown', onDocMouseDown, true);
    return () => document.removeEventListener('mousedown', onDocMouseDown, true);
  }, [folderId, collapseSelection]);

  const handleClick = (e) => {
    e.stopPropagation();
    if (isPlaceholder) {
      setEditValue('');
      clickCursorPosRef.current = null;
    } else if (inputRef.current) {
      clickCursorPosRef.current = inputRef.current.selectionStart;
    } else {
      clickCursorPosRef.current = editValue.length;
    }
    setIsLocalEditing(true);
  };

  const handleSave = () => {
    collapseSelection();
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== name) onRename?.(folderId, trimmed);
    setEditValue(trimmed || name || '');
    setIsLocalEditing(false);
    onFinishEdit?.();
  };

  const handleKeyDown = (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') {
      e.preventDefault();
      e.target.blur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      collapseSelection();
      setEditValue(name || '');
      setIsLocalEditing(false);
      onFinishEdit?.();
    }
  };

  return (
    <div className="atlas__node-name-wrap">
      {isEditing && editValue === '' && (
        <span className="atlas__node-name-hint">{editHint}</span>
      )}
      <textarea
        ref={inputRef}
        className={[
          'atlas__node-name-input',
          !isEditing && 'atlas__node-name-input--display',
          !isEditing && isPlaceholder && 'atlas__node-name-input--placeholder',
        ]
          .filter(Boolean)
          .join(' ')}
        value={isEditing ? editValue : displayValue}
        rows={1}
        readOnly={!isEditing}
        tabIndex={isEditing ? 0 : -1}
        title={displayValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={isEditing ? handleSave : undefined}
        onKeyDown={isEditing ? handleKeyDown : undefined}
        onClick={(e) => {
          e.stopPropagation();
          if (!isEditing) handleClick(e);
        }}
        onPointerDown={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      />
    </div>
  );
};

export default AtlasFolderName;
